import { useState, useEffect, useCallback } from 'react'
import { getConnections, connectGoogle, connectGoogleGmail, connectPlugin, disconnectService, toggleCalendarSync, toggleServiceSync } from '../../shared/api/connections'
import type { ConnectionInfo } from '../../shared/api/connections'
import type { CatalogPlugin, InstalledPlugin, PluginConnectionDetails } from './types'
import { setDefaultConnection, runPluginRunThrough } from './api'

/** Maps plugin IDs to their OAuth provider string when they differ */
const PLUGIN_PROVIDER_MAP: Record<string, string> = {
  'google-workspace': 'google',
  'microsoft-365': 'microsoft',
}

/** Check if a connection has email scopes granted (Gmail or Outlook). */
function hasEmailScopes(connection: ConnectionInfo): boolean {
  if (connection.provider === 'microsoft') {
    return connection.scopes?.some(s => s.includes('Mail.Read')) ?? false
  }
  return connection.scopes?.some(s => s.includes('gmail')) ?? false
}

/** Check if a connection has calendar scopes granted. */
function hasCalendarScopes(connection: ConnectionInfo): boolean {
  if (connection.provider === 'microsoft') {
    return connection.scopes?.some(s => s.includes('Calendars.Read')) ?? false
  }
  return connection.scopes?.some(s => s.includes('calendar')) ?? false
}

/** Check if a connection has contacts scopes granted. */
function hasContactsScopes(connection: ConnectionInfo): boolean {
  return connection.scopes?.some(s => s.includes('contacts')) ?? false
}

/** Build PluginConnectionDetails from a live ConnectionInfo */
function buildConnectionDetails(connection: ConnectionInfo, _pluginId: string): PluginConnectionDetails {
  return {
    connectionId: connection.connectionId,
    provider: connection.provider,
    displayName: connection.displayName || 'Connected',
    connectedAt: connection.connectedAt,
    calendars: connection.calendars ?? [],
    emailConnected: hasEmailScopes(connection),
    calendarConnected: hasCalendarScopes(connection),
    contactsConnected: hasContactsScopes(connection),
    calendarSyncEnabled: connection.calendarSyncEnabled ?? true,
    emailSyncEnabled: connection.emailSyncEnabled ?? true,
  }
}

interface UsePluginConnectionsParams {
  catalog: CatalogPlugin[]
  installed: InstalledPlugin[]
  installPlugin: (id: string) => Promise<void>
  refresh: () => void
}

export function usePluginConnections({ catalog, installed, installPlugin, refresh }: UsePluginConnectionsParams) {
  const [connections, setConnections] = useState<ConnectionInfo[]>([])
  const [connectingPlugin, setConnectingPlugin] = useState<string | null>(null)
  const [featureSelectPlugin, setFeatureSelectPlugin] = useState<CatalogPlugin | null>(null)

  // Fetch live connections
  const fetchConnections = useCallback(async () => {
    try {
      const result = await getConnections()
      setConnections(result.connections)
    } catch (err) {
      console.warn('[Plugins] Could not fetch connections:', err)
    }
  }, [])

  useEffect(() => {
    fetchConnections()
  }, [fetchConnections])

  // Handle OAuth callback redirect (?connected=<provider>)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('connected')) {
      window.history.replaceState({}, '', window.location.pathname)
      fetchConnections()
    }
  }, [fetchConnections])

  // Refresh connections when user returns from OAuth tab
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        fetchConnections()
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [fetchConnections])

  // Derive all connection details for a plugin (multi-account)
  const getConnectionsForPlugin = (pluginId: string): { primary: PluginConnectionDetails | null; all: PluginConnectionDetails[]; defaultConnectionId: string | null } => {
    const provider = PLUGIN_PROVIDER_MAP[pluginId] ?? pluginId
    const active = connections.filter(c => c.provider === provider && c.status === 'active')
    const all = active.map(c => buildConnectionDetails(c, pluginId))
    const defaultConnId = installed.find(p => p.id === pluginId)?.defaultConnectionId ?? null
    const primary = all.find(c => c.connectionId === defaultConnId) ?? all[0] ?? null
    return { primary, all, defaultConnectionId: defaultConnId }
  }

  const handleConnect = async (pluginId: string) => {
    // If plugin has feature groups, show selection modal instead of immediately connecting
    const catalogPlugin = catalog.find(p => p.id === pluginId)
    if (catalogPlugin?.featureGroups?.length) {
      setFeatureSelectPlugin(catalogPlugin)
      return
    }

    setConnectingPlugin(pluginId)
    try {
      const isInstalled = installed.some(p => p.id === pluginId)
      if (!isInstalled) {
        await installPlugin(pluginId)
      }
      // Use legacy endpoint for Google (redirect URI registered in Google Console)
      const result = pluginId === 'google-workspace'
        ? await connectGoogle()
        : await connectPlugin(pluginId)
      window.location.href = result.authUrl
    } catch (err) {
      console.error('Failed to start connection:', err)
    } finally {
      setConnectingPlugin(null)
    }
  }

  const handleFeatureConnect = async (selectedFeatures: string[]) => {
    if (!featureSelectPlugin) return
    const pluginId = featureSelectPlugin.id
    setConnectingPlugin(pluginId)
    try {
      const isInstalled = installed.some(p => p.id === pluginId)
      if (!isInstalled) {
        await installPlugin(pluginId)
      }
      // Google uses legacy endpoint; all other plugins use the generic endpoint with features
      const result = pluginId === 'google-workspace'
        ? await connectGoogle({ features: selectedFeatures })
        : await connectPlugin(pluginId, { features: selectedFeatures })
      window.location.href = result.authUrl
    } catch (err) {
      console.error('Failed to start connection:', err)
    } finally {
      setConnectingPlugin(null)
      setFeatureSelectPlugin(null)
    }
  }

  const handleConnectGmail = async () => {
    setConnectingPlugin('gmail')
    try {
      const result = await connectGoogleGmail()
      window.location.href = result.authUrl
    } catch (err) {
      console.error('Failed to start Gmail connection:', err)
    } finally {
      setConnectingPlugin(null)
    }
  }

  const handleConnectCalendar = async () => {
    setConnectingPlugin('calendar')
    try {
      const result = await connectGoogle({ features: ['calendar'] })
      window.location.href = result.authUrl
    } catch (err) {
      console.error('Failed to start Calendar connection:', err)
    } finally {
      setConnectingPlugin(null)
    }
  }

  const handleConnectContacts = async () => {
    setConnectingPlugin('contacts')
    try {
      const result = await connectGoogle({ features: ['contacts'] })
      window.location.href = result.authUrl
    } catch (err) {
      console.error('Failed to start Contacts connection:', err)
    } finally {
      setConnectingPlugin(null)
    }
  }

  const handleDisconnect = async (connectionId?: string) => {
    if (!connectionId) return
    try {
      await disconnectService(connectionId)
      setConnections(prev => prev.filter(c => c.connectionId !== connectionId))
    } catch (err) {
      console.error('Failed to disconnect:', err)
    }
  }

  const handleSetDefault = async (pluginId: string, connectionId: string) => {
    try {
      await setDefaultConnection(pluginId, connectionId)
      await refresh()
    } catch (err) {
      console.error('Failed to set default connection:', err)
    }
  }

  const handleToggleCalendarSync = async (calendarId: string, syncEnabled: boolean) => {
    // Optimistic update
    setConnections(prev => prev.map(c => ({
      ...c,
      calendars: c.calendars?.map(cal =>
        cal.id === calendarId ? { ...cal, syncEnabled } : cal,
      ),
    })))
    try {
      await toggleCalendarSync(calendarId, syncEnabled)
    } catch (err) {
      console.error('Failed to toggle calendar sync:', err)
      // Revert
      setConnections(prev => prev.map(c => ({
        ...c,
        calendars: c.calendars?.map(cal =>
          cal.id === calendarId ? { ...cal, syncEnabled: !syncEnabled } : cal,
        ),
      })))
    }
  }

  const handleToggleServiceSync = async (connectionId: string, services: { calendarSyncEnabled?: boolean; emailSyncEnabled?: boolean }) => {
    // Optimistic update
    setConnections(prev => prev.map(c =>
      c.connectionId === connectionId
        ? {
            ...c,
            calendarSyncEnabled: services.calendarSyncEnabled ?? c.calendarSyncEnabled,
            emailSyncEnabled: services.emailSyncEnabled ?? c.emailSyncEnabled,
          }
        : c,
    ))
    try {
      await toggleServiceSync(connectionId, services)
    } catch (err) {
      console.error('Failed to toggle service sync:', err)
      // Revert
      setConnections(prev => prev.map(c =>
        c.connectionId === connectionId
          ? {
              ...c,
              calendarSyncEnabled: services.calendarSyncEnabled !== undefined ? !services.calendarSyncEnabled : c.calendarSyncEnabled,
              emailSyncEnabled: services.emailSyncEnabled !== undefined ? !services.emailSyncEnabled : c.emailSyncEnabled,
            }
          : c,
      ))
    }
  }

  const handleRunThrough = async (pluginId: string) => {
    await runPluginRunThrough(pluginId)
  }

  return {
    connections,
    connectingPlugin,
    featureSelectPlugin,
    setFeatureSelectPlugin,
    getConnectionsForPlugin,
    handleConnect,
    handleFeatureConnect,
    handleConnectGmail,
    handleConnectCalendar,
    handleConnectContacts,
    handleDisconnect,
    handleSetDefault,
    handleToggleCalendarSync,
    handleToggleServiceSync,
    handleRunThrough,
  }
}
